import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Divider,
  Stack,
  Box,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import TimeBoundDetails from './TimeBoundDetails';
import SequenceWithExclusionDetails from './SequenceWithExclusionDetails';
import SimultaneousSignalsDetails from './SimultaneousSignalsDetails';
import BullishBearishDetails from './BullishBearishDetails'; // Add this import
import { CONDITION_TYPES, inputSx, selectSx, cardSx, TV } from '../../constants';
// import { validateStrategyIdFormat } from '../../utils/validation';

const SubStrategyCard = ({ sub, idx, onUpdate, onRemove, bulkStrategyTimeframe, isConfirmation, isRequired, idValidation, onValidateId }) => {
  const [expanded, setExpanded] = useState(false);

  const conditionType = sub.condition?.type || 'time_bound';
  const conditionTypeLabel = CONDITION_TYPES.find(ct => ct.value === conditionType)?.label || 'Unknown';
  
  const idStatus = idValidation?.[idx] || { isValid: false, error: '', checked: false };

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2, pb: 1, cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ 
                fontSize: '13px', 
                fontWeight: 700, 
                color: idStatus.isValid ? TV.accent.success : TV.text.primary,
                transition: 'color 0.2s'
              }}>
                {sub.strategy_id || `Condition ${idx + 1}`}
              </Typography>
              {isConfirmation && <Chip label="CONFIRMATION" size="small" sx={{ bgcolor: TV.accent.success, color: '#000', fontSize: '10px' }} />}
              {isRequired && <Chip label="REQUIRED" size="small" sx={{ bgcolor: TV.accent.warning, color: '#000', fontSize: '10px' }} />}
              {!sub.strategy_id && <Chip label="NO ID" size="small" sx={{ bgcolor: TV.accent.danger, color: '#fff', fontSize: '10px' }} />}
            </Stack>
            <Typography sx={{ fontSize: '11px', color: TV.text.tertiary, mt: 0.5 }}>
              {conditionTypeLabel}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button 
              size="small" 
              onClick={(e) => { e.stopPropagation(); onRemove(idx); }}
              sx={{ color: TV.accent.danger, minWidth: '32px', p: 1 }}
            >
              <DeleteIcon sx={{ fontSize: '16px' }} />
            </Button>
            <IconButton 
              size="small"
              onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
              sx={{ color: TV.accent.primary }}
            >
              <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
            </IconButton>
          </Stack>
        </Stack>
      </CardContent>

      {expanded && (
        <>
          <Divider sx={{ borderColor: TV.border.primary }} />
          <CardContent sx={{ pt: 2, pb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={0.5}>
                  <TextField 
                    fullWidth 
                    size="small" 
                    label="Strategy ID" 
                    placeholder="e.g., my_early_signal"
                    value={sub.strategy_id || ''} 
                    onChange={(e) => {
                      onUpdate(idx, 'strategy_id', e.target.value);
                      if (onValidateId) {
                        onValidateId(idx, e.target.value);
                      }
                    }}
                    onBlur={(e) => {
                      if (onValidateId) {
                        onValidateId(idx, e.target.value);
                      }
                    }}
                    error={!idStatus.isValid && idStatus.checked}
                    helperText={
                      (idStatus.checked && idStatus.error) || 
                      '3-100 chars, alphanumeric + underscore/hyphen'
                    }
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root': {
                        ...inputSx['& .MuiOutlinedInput-root'],
                        borderColor: !idStatus.isValid && idStatus.checked 
                          ? TV.accent.danger 
                          : (idStatus.isValid && idStatus.checked 
                            ? TV.accent.success 
                            : undefined)
                      }
                    }}
                  />
                  {idStatus.isValid && idStatus.checked && (
                    <Typography sx={{ fontSize: '11px', color: TV.accent.success, fontWeight: 500 }}>
                      ✓ Valid strategy ID
                    </Typography>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Condition Type</InputLabel>
                  <Select 
                    value={conditionType} 
                    label="Condition Type"
                    onChange={(e) => {
                      const newType = e.target.value;
                      const baseCondition = {
                        type: newType,
                        name: sub.condition?.name || `condition_${idx}`,
                        enabled: true,
                      };
                      
                      if (newType === 'time_bound') {
                        baseCondition.early_signal = { name: '', timeframe: bulkStrategyTimeframe };
                        baseCondition.confirmation_signal = { name: '', timeframe: bulkStrategyTimeframe };
                        baseCondition.min_minutes = 0;
                        baseCondition.max_minutes = 60;
                      } else if (newType === 'sequence_with_exclusion') {
                        baseCondition.signals = [];
                        baseCondition.excluded_signals = [];
                        baseCondition.min_minutes = 0;
                        baseCondition.max_minutes = 60;
                        baseCondition.reset_on_excluded = true;
                      } else if (newType === 'simultaneous_signals') {
                        baseCondition.signals = [];
                        baseCondition.time_tolerance_ms = 1000;
                        baseCondition.order_matters = false;
                      }
                      
                      onUpdate(idx, 'condition', baseCondition);
                    }}
                    sx={selectSx}
                  >
                    {CONDITION_TYPES.map(ct => <MenuItem key={ct.value} value={ct.value}>{ct.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ height: '1px', bgcolor: TV.border.primary }} />
              </Grid>

              {conditionType === 'time_bound' && (
                <Grid item xs={12}>
                  <TimeBoundDetails sub={sub} idx={idx} onUpdate={onUpdate} bulkStrategyTimeframe={bulkStrategyTimeframe} />
                </Grid>
              )}

              {conditionType === 'sequence_with_exclusion' && (
                <Grid item xs={12}>
                  <SequenceWithExclusionDetails sub={sub} idx={idx} onUpdate={onUpdate} bulkStrategyTimeframe={bulkStrategyTimeframe} />
                </Grid>
              )}

              {conditionType === 'simultaneous_signals' && (
                <Grid item xs={12}>
                  <SimultaneousSignalsDetails sub={sub} idx={idx} onUpdate={onUpdate} />
                </Grid>
              )}

              {conditionType === 'is_bullish_when_signal_arrived' && (
                <Grid item xs={12}>
                  <BullishBearishDetails 
                    sub={sub} 
                    idx={idx} 
                    onUpdate={onUpdate} 
                    isBullish={true} 
                  />
                </Grid>
              )}

              {conditionType === 'is_bearish_when_signal_arrived' && (
                <Grid item xs={12}>
                  <BullishBearishDetails 
                    sub={sub} 
                    idx={idx} 
                    onUpdate={onUpdate} 
                    isBullish={false} 
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </>
      )}
    </Card>
  );
};

export default SubStrategyCard;
